import { Injectable } from '@angular/core';
import { Address } from '@liberty-financial/agp-shared';
import { Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';

import { ApiHttp } from '@core/api/api-http';

import {
  opportunityAddressSchemaContractToAddress,
  OpportunityAddressSchemaWithIdContract
} from './addressContract';
import { Collection, flattenCollection } from './contract';
import {
  QuestionnaireContract,
  questionnaireContractToDomain
} from '../components/questionnaire-form/models/questionnaireContract';
import { Questionnaire } from '../components/questionnaire-form/models/questionnaire';
import { OpportunityApiHttp } from '@core/api/opportunity-api-http';

export type SplitContract = {
  id: string;
  providerId: number;
  providerName: string;
  productId: number;
  productName: string;
  splitAmount: number;
  interestRate: number;
  fixedTermMonths: number | null;
  interestOnlyTermMonths: number | null;
  loanTermMonths: number;
  repaymentAmount: number;
  frequency: string;
  settlementDate?: string;
  lenderReference?: string;
  status?: string;
  originalStatus?: string;
  maxLoanTerm?: number;
  maxInterestOnlyTerm?: number;
  isReverseMortgage?: boolean;
};

export type CapContract = {
  recipient: string;
  allowGift: boolean;
  personalMessage: string | null;
};

export type ApplicantAddress = {
  applicantId: string;
  applicantName: string;
  addressId: string;
  addressDescription: string;
};

export type Submission = {
  questionnaire: Questionnaire | undefined;
  summary: string | undefined;
  productId: string;
  cap: CapContract | undefined;
};

export type UnconditionalContract = {
  unconditional: {
    settlementDate: string;
    settlementAmount: number;
    accountName: string;
    accountReference: string | undefined;
    lenderReference: string | undefined;
    splits: Collection;
    postSettlementAddress: Collection;
  };
  cap?: CapContract;
  productSelection?: {
    recommendation?: { productSelection: string };
  };
};

export interface PostSettlementAddress {
  applicantId: string;
  address: Address | null;
}

export type Unconditional = {
  settlementDate: string;
  settlementAmount: number;
  accountName: string;
  accountReference: string;
  lenderReference: string;
  splits: SplitContract[];
  postSettlementAddress: PostSettlementAddress[];
  cap?: CapContract;
  recommendedProductSelectionId?: string;
};
@Injectable({
  providedIn: 'root'
})
export class OpportunityWorkflowService {
  constructor(private http: OpportunityApiHttp) {}

  getSubmissionQuestions(id: string): Observable<Questionnaire> {
    return this.http
      .get<QuestionnaireContract>(
        `opportunities/${id}/submission/questionnaire`,
        ApiHttp.getStandardOptions()
      )
      .pipe(map((x) => questionnaireContractToDomain(x)));
  }

  updateSubmission(
    opportunityId: string,
    submission: Submission
  ): Observable<any> {
    return this.http.put(
      `opportunities/${opportunityId}`,
      {
        submission: {
          questionnaire: submission.questionnaire,
          summary: submission.summary,
          productId: submission.productId
        },
        cap: submission.cap
      },
      ApiHttp.getStandardOptions()
    );
  }

  readyToAssess(opportunityId: string, submission: any): Observable<any> {
    return this.http.post(
      `opportunities/${opportunityId}/ready-to-assess`,
      submission,
      ApiHttp.getStandardOptions()
    );
  }

  submitExtraDocuments(
    opportunityId: string,
    submission: any
  ): Observable<any> {
    return this.http.post(
      `opportunities/${opportunityId}/submit-extra-documents`,
      submission,
      ApiHttp.getStandardOptions()
    );
  }

  submit(id: string): Observable<any> {
    return this.http.post(
      `opportunities/${id}/submit`,
      null,
      ApiHttp.getStandardOptionsWithResponse()
    );
  }

  amend(id: string): Observable<any> {
    return this.http.post(
      `opportunities/${id}/amend`,
      null,
      ApiHttp.getStandardOptionsWithResponse()
    );
  }

  markAsSettled(id: string): Observable<any> {
    return this.http.post(
      `opportunities/${id}/mark-settled`,
      null,
      ApiHttp.getStandardOptionsWithResponse()
    );
  }

  withdraw(id: string, payload: any): Observable<any> {
    return this.http.post(
      `opportunities/${id}/withdraw`,
      payload,
      ApiHttp.getStandardOptionsWithResponse()
    );
  }

  preApproval(id: string): Observable<any> {
    return this.http.post(
      `opportunities/${id}/pre-approval`,
      null,
      ApiHttp.getStandardOptionsWithResponse()
    );
  }

  approved(id: string): Observable<any> {
    return this.http.post(
      `opportunities/${id}/approved`,
      null,
      ApiHttp.getStandardOptionsWithResponse()
    );
  }

  getExternalApplicationLink(opportunityId: string): Observable<string> {
    return this.http.getText(`opportunities/${opportunityId}/application-url`);
  }

  delete(id: string) {
    //TODO: Change it to API
    return of({});
  }

  createUnconditional(opportunityId: string): Observable<any> {
    return this.http.put(
      `opportunities/${opportunityId}/unconditional/create`,
      null,
      ApiHttp.getStandardOptions()
    );
  }

  getUnconditional(opportunityId: string): Observable<Unconditional> {
    return this.http
      .get<UnconditionalContract>(`opportunities/${opportunityId}`, {
        headers: ApiHttp.getStandardOptions().headers,
        params: {
          depth: 6
        }
      })
      .pipe(
        map((x) => {
          return {
            settlementDate: x.unconditional.settlementDate,
            settlementAmount: x.unconditional.settlementAmount,
            accountName: x.unconditional.accountName,
            accountReference: x.unconditional.accountReference ?? '',
            lenderReference: x.unconditional.lenderReference ?? '',
            splits: flattenCollection<SplitContract>(x.unconditional.splits),
            postSettlementAddress:
              flattenCollection<OpportunityAddressSchemaWithIdContract>(
                x.unconditional.postSettlementAddress
              ).map((v) => ({
                applicantId: v.id,
                address: opportunityAddressSchemaContractToAddress(v)
              })),
            cap: x.cap,
            recommendedProductSelectionId:
              x.productSelection?.recommendation?.productSelection
          };
        })
      );
  }

  updateUnconditional(
    opportunityId: string,
    payload: Unconditional,
    isMultiSplit: boolean = false
  ): Observable<any> {
    return this.http.post(
      `opportunities/${opportunityId}/unconditional`,
      {
        unconditional: {
          settlementDate: isMultiSplit ? undefined : payload.settlementDate,
          settlementAmount: payload.settlementAmount,
          accountName: payload.accountName,
          accountReference: payload.accountReference,
          lenderReference: isMultiSplit ? undefined : payload.lenderReference,
          splits: payload.splits ?? undefined,
          postSettlementAddress: payload.postSettlementAddress
        },
        cap: payload.cap
      },
      ApiHttp.getStandardOptions()
    );
  }
}
